import { VenueType } from "@/lib/shared/enums";

/**
 * File extensions excluded by default from quote attachment listings. A vendor's HTML export
 * (e.g. "proposal.html") can't be safely rendered or linked to directly — its stored
 * Content-Type can't be trusted, and it may originate from an external upload. Display call
 * sites get these dropped automatically; a caller that genuinely needs the full inventory (e.g.
 * a storage "keep list") must opt out explicitly — see `getQuoteAttachmentsByEventAndVenue`.
 */
export const HTML_ATTACHMENT_EXTENSIONS = new Set(["html", "htm"]);

/**
 * Quote sections for the quote form.
 */
export enum QuoteSection {
  RequiredInformation = 1,
  Guestroom = 2,
  MeetingRoom = 3,
  FoodAndBeverage = 4,
  OptionalInformation = 5,
}

/**
 * Guestroom option types.
 */
export enum GuestroomOption {
  NightlyRates = "nightly_rates",
  TotalCost = "total_cost",
  NoRooms = "no_rooms",
}

/**
 * Meeting room option types.
 */
export enum MeetingRoomOption {
  Rates = "rates",
  TotalCost = "total_cost",
  NoRooms = "no_rooms",
  Waived = "waived",
}

/**
 * Food and beverage option types.
 */
export enum FoodAndBeverageOption {
  Minimum = "minimum",
  NoFoodAndBeverage = "no_food_and_beverage",
}

/**
 * Tax type options.
 */
export enum TaxType {
  PreTax = "pre_tax",
  PostTax = "post_tax",
}

/**
 * Room type options.
 */
export enum RoomType {
  Single = "single",
  Double = "double",
  Twin = "twin",
  ROH = "roh",
  Suite = "suite",
  Staff = "staff",
  VIP = "vip",
}

/**
 * Room type options for dropdowns.
 */
export const ROOM_TYPE_OPTIONS: { value: RoomType; label: string }[] = [
  { value: RoomType.Single, label: "Single" },
  { value: RoomType.Double, label: "Double" },
  { value: RoomType.Twin, label: "Twin" },
  { value: RoomType.ROH, label: "Run of House" },
  { value: RoomType.Suite, label: "Suite" },
  { value: RoomType.Staff, label: "Staff" },
  { value: RoomType.VIP, label: "VIP" },
];

/**
 * Array of all room types.
 */
export const ROOM_TYPES: RoomType[] = [
  RoomType.Single,
  RoomType.Double,
  RoomType.Twin,
  RoomType.ROH,
  RoomType.Suite,
  RoomType.Staff,
  RoomType.VIP,
];

/**
 * Quote status options.
 */
export enum OeeQuoteStatus {
  Draft = "draft",
  Estimated = "estimated",
  Negotiated = "negotiated",
}

/**
 * Quote source constants.
 */
export const QUOTE_SOURCE = {
  VENUE_QUOTE_CALCULATOR: "venue_quote_calculator",
  OPS_QUOTE_CALCULATOR: "ops_quote_calculator",
} as const;

/**
 * Path prefix of the public, token-gated venue quote calculator. Anything under
 * this prefix is a venue filling in their own quote; everything else (notably
 * `/ava/quotes/add/...`) is the ops-facing calculator.
 */
export const VENUE_QUOTE_PATH_PREFIX = "/quotes";

/** Path prefix of the ops-facing quote calculator. */
export const AVA_QUOTE_PATH_PREFIX = "/ava/quotes";

/**
 * Maximum length of a free-text value rendered into a Slack field. Slack
 * truncates very long attachment fields itself, but venue-supplied text reaches
 * these messages, so it is capped before it is sent.
 */
export const MAX_SLACK_FIELD_LENGTH = 500;

/**
 * Upper bound on Slack messages sent for a single auto-extraction. One document
 * legitimately yields a handful of quotes; anything beyond this is a bug or an
 * abuse attempt, and the channel should not absorb it.
 */
export const MAX_AUTO_EXTRACT_NOTIFICATIONS = 5;

/**
 * Upper bound on a free-text quote field (concessions, open items, etc.) written
 * into the `extraction_data` JSONB blob. The blob is fetched wholesale by the
 * customer portal on every load, so planner-supplied text is capped server-side
 * before it is stored — the client textarea limit is not enforcement, since the
 * save action is directly callable.
 */
export const MAX_QUOTE_FREETEXT_LENGTH = 5000;

/**
 * Routes that render quote data, revalidated whenever a quote is written.
 */
export const QUOTE_REVALIDATE_PATHS = [
  "/ava/quotes/add/[event_id]/[venue_static_id]",
  "/ava/quotes/review",
  "/[event_id]/quote-dashboard",
];

/**
 * Routes whose content depends on `events.event_status`, revalidated when a new
 * quote moves an event on to the quote dashboard stage.
 */
export const EVENT_STAGE_PATHS = ["/[event_id]/narrow-options", "/[event_id]/quote-dashboard", "/[event_id]/dashboard"];

/** Left border colour of a successful quote card in Slack. */
export const SLACK_CARD_COLOR = "#6366F1";

/** Left border colour of a failed-extraction card in Slack. */
export const SLACK_FAILURE_CARD_COLOR = "#DC2626";

/** Cap on how long a stalled Slack webhook may hold the serverless instance open. */
export const SLACK_WEBHOOK_TIMEOUT_MS = 5000;

/**
 * Which venue-quote event a Slack notification is reporting.
 */
export enum VenueQuoteNotificationKind {
  Created = "created",
  Updated = "updated",
  AutoExtracted = "auto_extracted",
}

/**
 * Human-readable labels for VenueType values, in sentence case.
 * Brand names (Airbnb, Vrbo, Hipcamp, Glamping Hub) retain their canonical
 * capitalization since they are proper nouns; words following a brand are
 * sentence-cased (e.g. "Airbnb experiences", not "Airbnb Experiences").
 *
 * Intentionally typed as Record<VenueType, string> so adding a new value
 * to the VenueType enum without a label here is a compile-time error.
 */
export const VENUE_TYPE_LABELS: Record<VenueType, string> = {
  [VenueType.HOTEL]: "Hotel",
  [VenueType.RESTAURANT]: "Restaurant",
  [VenueType.AIRBNB]: "Airbnb",
  [VenueType.AIRBNB_EXPERIENCES]: "Airbnb experiences",
  [VenueType.AIRBNB_SERVICES]: "Airbnb services",
  [VenueType.VRBO]: "Vrbo",
  [VenueType.VILLA]: "Villa",
  [VenueType.HIPCAMP]: "Hipcamp",
  [VenueType.GLAMPINGHUB]: "Glamping Hub",
  [VenueType.COWORKING]: "Coworking",
  [VenueType.OTHER]: "Other",
  [VenueType.ACTIVITY]: "Activity",
  [VenueType.EVENT_SPACE]: "Event space",
  [VenueType.CATERING]: "Catering",
  [VenueType.VENDOR]: "Vendor",
  [VenueType.RANCH]: "Ranch",
  [VenueType.YURT]: "Yurt",
  [VenueType.VACATION_RENTAL]: "Vacation rental",
  [VenueType.BAR]: "Bar",
  [VenueType.NIGHTCLUB]: "Nightclub",
  [VenueType.RESORT]: "Resort",
};

/**
 * Returns the sentence-case label for a venue type, or undefined if none provided.
 *
 * @param venueType - VenueType value from the database, or undefined
 * @returns Human-readable label, or undefined when input is undefined
 */
export const formatVenueType = (venueType: VenueType | undefined): string | undefined =>
  venueType ? VENUE_TYPE_LABELS[venueType] : undefined;

/** Matches a guestroom fee's free-text name, e.g. "Resort Fee", "Resort & Amenity Fee". */
export const RESORT_FEE_NAME_PATTERN = /resort/i;

/**
 * How long the confirmation stays up before the calculator reloads itself.
 *
 * Long enough to read "Saved", short enough that nobody wonders whether to
 * press again. The reload is what clears the form and shows the quote as it was
 * stored, so it is part of the submit rather than a nicety.
 */
export const RELOAD_AFTER_SUBMIT_MS = 1000;

/**
 * Upper bound on quotes one "we received your proposal" email covers. A form
 * submit writes one quote and an auto-extraction a handful (see
 * {@link MAX_AUTO_EXTRACT_NOTIFICATIONS}); ids past this are ignored.
 */
export const MAX_QUOTE_RECEIVED_QUOTES = 5;

/**
 * Only quotes written this recently are confirmed. The extraction path's ids
 * reach the email from a public endpoint, so a replay naming old quotes must
 * not mail the hotel; a submit is confirmed well inside this window.
 */
export const QUOTE_RECEIVED_RECENCY_MS = 30 * 60 * 1000;
